import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatNumber } from '../../utils/formatters';
import EmptyState from '../shared/EmptyState';
import './ActivityReportTable.css';

// Renders a count with thousands separators; zero values are muted so a
// row full of real activity doesn't get visually drowned out by "0"s.
const NumCell: React.FC<{ value?: number }> = ({ value }) => {
  const v = value ?? 0;
  return <td className={`art-num${v === 0 ? ' art-num-zero' : ''}`}>{formatNumber(v)}</td>;
};

interface ActivityRow {
  sort_key: string;
  period: string;
  user: string;
  tc_created?: number;
  tc_executed?: number;
  sc_created?: number;
  sc_executed?: number;
  runs_created?: number;
  runs_completed?: number;
  tc_ai?: number;
  tc_manual?: number;
  tc_ai_pct?: number;
  tc_manual_pct?: number;
  sc_ai?: number;
  sc_manual?: number;
  sc_ai_pct?: number;
  sc_manual_pct?: number;
}

type Period = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
type View = 'overall' | 'by_user' | 'ai_vs_manual';

export const ActivityReportTable: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [period, setPeriod] = useState<Period>('monthly');
  const [view, setView] = useState<View>('overall');
  const [monthsBack, setMonthsBack] = useState(12);
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [userFilter, setUserFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get(`/api/v1/dashboard/activity-report?period=${period}&view=${view}&months_back=${monthsBack}`)
      .then(resp => {
        if (cancelled) return;
        const data = resp.data as any;
        setRows(data.rows || []);
        setUsers(data.users || []);
        setUserFilter('');
        setMonthFilter('');
      })
      .catch(() => { if (!cancelled) { setRows([]); setUsers([]); setError('Failed to load activity report.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, view, monthsBack]);

  // Distinct months present in the currently loaded range (Monthly period
  // only) — sorted newest first, deduped across users/AI-vs-Manual rows.
  const monthOptions = period === 'monthly'
    ? Array.from(new Map(rows.map(r => [r.sort_key, r.period])).entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
    : [];

  const handleExport = async () => {
    if (!isAdmin) return;
    setExporting(true);
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const tokens = authTokens ? JSON.parse(authTokens) : null;
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(
        `${apiBase}/api/v1/dashboard/export-report?period=${period}&view=${view}&months_back=${monthsBack}`,
        { headers: { Authorization: `Bearer ${tokens?.access_token}` } }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assurex_report_${view}_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const filtered = rows.filter(r =>
    (!userFilter || r.user === userFilter) &&
    (!monthFilter || r.sort_key === monthFilter)
  );

  // AI vs Manual — group by period, inject a bolded "Overall" summary row per period
  const renderAiVsManualRows = () => {
    const periodOrder: string[] = [];
    const byPeriod: Record<string, ActivityRow[]> = {};
    filtered.forEach(r => {
      if (!byPeriod[r.sort_key]) { byPeriod[r.sort_key] = []; periodOrder.push(r.sort_key); }
      byPeriod[r.sort_key].push(r);
    });

    const out: React.ReactNode[] = [];
    periodOrder.forEach(sk => {
      const group = byPeriod[sk];
      const totalTcAi = group.reduce((s, r) => s + (r.tc_ai ?? 0), 0);
      const totalTcManual = group.reduce((s, r) => s + (r.tc_manual ?? 0), 0);
      const totalScAi = group.reduce((s, r) => s + (r.sc_ai ?? 0), 0);
      const totalScManual = group.reduce((s, r) => s + (r.sc_manual ?? 0), 0);
      const tcTotal = totalTcAi + totalTcManual;
      const scTotal = totalScAi + totalScManual;
      const tcAiPct = tcTotal > 0 ? Math.round((totalTcAi / tcTotal) * 1000) / 10 : 0;
      const tcManualPct = tcTotal > 0 ? Math.round((totalTcManual / tcTotal) * 1000) / 10 : 0;
      const scAiPct = scTotal > 0 ? Math.round((totalScAi / scTotal) * 1000) / 10 : 0;
      const scManualPct = scTotal > 0 ? Math.round((totalScManual / scTotal) * 1000) / 10 : 0;
      const periodLabel = group[0].period;

      out.push(
        <tr key={`${sk}-summary`} className="art-row-summary">
          <td className="art-period">{periodLabel}</td>
          {view === 'ai_vs_manual' && <td className="art-summary-label">Overall</td>}
          <td className="art-num art-summary-num">{formatNumber(totalTcAi)}</td>
          <td className="art-pct art-pct-ai"><span className="art-pct-badge">{tcAiPct}%</span></td>
          <td className="art-num art-summary-num">{formatNumber(totalTcManual)}</td>
          <td className="art-pct art-pct-manual"><span className="art-pct-badge">{tcManualPct}%</span></td>
          <td className="art-num art-summary-num">{formatNumber(totalScAi)}</td>
          <td className="art-pct art-pct-ai"><span className="art-pct-badge">{scAiPct}%</span></td>
          <td className="art-num art-summary-num">{formatNumber(totalScManual)}</td>
          <td className="art-pct art-pct-manual"><span className="art-pct-badge">{scManualPct}%</span></td>
        </tr>
      );

      group.forEach((row, i) => {
        out.push(
          <tr key={`${sk}-${i}`}>
            <td className="art-period art-period-indent">{row.period}</td>
            <td className="art-user">{row.user}</td>
            <NumCell value={row.tc_ai} />
            <td className="art-pct art-pct-ai"><span className="art-pct-badge">{row.tc_ai_pct ?? 0}%</span></td>
            <NumCell value={row.tc_manual} />
            <td className="art-pct art-pct-manual"><span className="art-pct-badge">{row.tc_manual_pct ?? 0}%</span></td>
            <NumCell value={row.sc_ai} />
            <td className="art-pct art-pct-ai"><span className="art-pct-badge">{row.sc_ai_pct ?? 0}%</span></td>
            <NumCell value={row.sc_manual} />
            <td className="art-pct art-pct-manual"><span className="art-pct-badge">{row.sc_manual_pct ?? 0}%</span></td>
          </tr>
        );
      });
    });
    return out;
  };

  return (
    <div className="art-card">
      <div className="art-header">
        <div className="art-header-title">
          <div className="art-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div>
            <h3>Activity Report</h3>
            <p>Test case, scenario, and test run velocity over time</p>
          </div>
        </div>
        <button
          className={`art-export-btn${!isAdmin ? ' art-export-disabled' : ''}`}
          onClick={handleExport}
          disabled={!isAdmin || exporting}
          title={isAdmin ? 'Export this report as CSV' : 'Only Admin users can export reports'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <div className="art-controls">
        <div className="art-control-group">
          <label>Period</label>
          <select value={period} onChange={e => setPeriod(e.target.value as Period)} className="art-select">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="art-control-group">
          <label>View</label>
          <select value={view} onChange={e => setView(e.target.value as View)} className="art-select">
            <option value="overall">Overall</option>
            <option value="by_user">By User</option>
            <option value="ai_vs_manual">AI vs Manual</option>
          </select>
        </div>
        <div className="art-control-group">
          <label>Range</label>
          <select value={monthsBack} onChange={e => setMonthsBack(Number(e.target.value))} className="art-select">
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
          </select>
        </div>
        {period === 'monthly' && monthOptions.length > 0 && (
          <div className="art-control-group">
            <label>Month</label>
            <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="art-select">
              <option value="">All months</option>
              {monthOptions.map(([sortKey, label]) => (
                <option key={sortKey} value={sortKey}>{label}</option>
              ))}
            </select>
          </div>
        )}
        {(view === 'by_user' || view === 'ai_vs_manual') && users.length > 0 && (
          <div className="art-control-group">
            <label>User</label>
            <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="art-select">
              <option value="">All</option>
              {users.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        )}
      </div>

      {error && <div className="art-error">{error}</div>}

      {loading ? (
        <div className="art-skeleton" />
      ) : filtered.length === 0 ? (
        <EmptyState message="No activity data for this period." />
      ) : (
        <div className="art-table-wrap">
          <table className="art-table">
            <thead>
              <tr>
                <th>Period</th>
                {(view === 'by_user' || view === 'ai_vs_manual') && <th>User</th>}
                {view === 'ai_vs_manual' ? (
                  <>
                    <th className="art-th-num" title="Test Cases created by AI">TC (AI)</th>
                    <th className="art-th-num" title="Share of Test Cases created by AI">TC AI %</th>
                    <th className="art-th-num" title="Test Cases created manually">TC (Manual)</th>
                    <th className="art-th-num" title="Share of Test Cases created manually">TC Manual %</th>
                    <th className="art-th-num" title="Test Scenarios created by AI">TS (AI)</th>
                    <th className="art-th-num" title="Share of Test Scenarios created by AI">TS AI %</th>
                    <th className="art-th-num" title="Test Scenarios created manually">TS (Manual)</th>
                    <th className="art-th-num" title="Share of Test Scenarios created manually">TS Manual %</th>
                  </>
                ) : (
                  <>
                    <th className="art-th-num" title="Test Cases created">TC</th>
                    <th className="art-th-num" title="Test Cases executed">TC Executed</th>
                    <th className="art-th-num" title="Test Scenarios created">TS</th>
                    <th className="art-th-num" title="Test Scenarios executed">TS Executed</th>
                    <th className="art-th-num" title="Test Runs created">Runs Created</th>
                    <th className="art-th-num" title="Test Runs completed">Runs Completed</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {view === 'ai_vs_manual' ? renderAiVsManualRows() : filtered.map((row, i) => (
                <tr key={i}>
                  <td className="art-period">{row.period}</td>
                  {view === 'by_user' && <td className="art-user">{row.user}</td>}
                  <NumCell value={row.tc_created} />
                  <NumCell value={row.tc_executed} />
                  <NumCell value={row.sc_created} />
                  <NumCell value={row.sc_executed} />
                  <NumCell value={row.runs_created} />
                  <NumCell value={row.runs_completed} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityReportTable;
